/**
 * AdminMis — Phases 1+2+4+6 (Foundation + Core Beta Intel + Engine Health
 * + Daily Digest). Reuses _shared primitives; no changes to other modules.
 */
import React, { useState, useMemo, useCallback } from "react";
import { PageHeader, KpiCard, KpiGrid, Panel, Table, RefreshButton,
         useAdminQuery, Unavailable, INDIGO, MUTE } from "../_shared";
import { api } from "@/hooks/useAuth";
import { toast } from "sonner";

const PRESETS = [
  { k: "today", label: "Today" },
  { k: "yesterday", label: "Yesterday" },
  { k: "7d", label: "7 Days" },
  { k: "30d", label: "30 Days" },
];
const TABS = [
  { k: "overview",  label: "Overview" },
  { k: "funnel",    label: "Funnel" },
  { k: "retention", label: "Retention" },
  { k: "users",     label: "Users" },
  { k: "coupons",   label: "Coupons & Credits" },
  { k: "engine",    label: "Engine Health" },
  { k: "digest",    label: "Daily Digest" },
  { k: "registry",  label: "Internal Accounts" },
];

function arr(x) { return Array.isArray(x) ? x : []; }

function FilterBar({ preset, setPreset, exclude, setExclude, label }) {
  return (
    <div data-testid="mis-filter-bar" style={{
      display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap",
      padding: "10px 14px", background: "#F2EEFF", borderRadius: 12,
      border: "1px solid rgba(93,0,255,0.14)", marginBottom: 16,
    }}>
      <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
        {PRESETS.map((p) => (
          <button key={p.k} data-testid={`mis-preset-${p.k}`}
                  onClick={() => setPreset(p.k)}
                  style={{ padding: "6px 12px", borderRadius: 999, border: 0,
                           cursor: "pointer", fontSize: 12,
                           background: preset === p.k ? INDIGO : "white",
                           color: preset === p.k ? "white" : "#1C1C1C",
                           fontWeight: 500 }}>{p.label}</button>
        ))}
      </div>
      <label data-testid="mis-exclude-toggle" style={{
        display: "inline-flex", alignItems: "center", gap: 6,
        marginLeft: "auto", fontSize: 12, color: "#1C1C1C",
      }}>
        <input type="checkbox" checked={exclude}
               onChange={(e) => setExclude(e.target.checked)} />
        Exclude Internal & Test
      </label>
      <span style={{ fontSize: 11, color: MUTE }}>IST · {label || ""}</span>
    </div>
  );
}

function TabBar({ active, setActive }) {
  return (
    <div data-testid="mis-tab-bar" style={{
      display: "flex", gap: 4, flexWrap: "wrap", marginBottom: 14,
      borderBottom: "1px solid #EEEBF5", paddingBottom: 2,
    }}>
      {TABS.map((t) => (
        <button key={t.k} data-testid={`mis-tab-${t.k}`}
                onClick={() => setActive(t.k)}
                style={{ padding: "8px 14px", borderRadius: "10px 10px 0 0",
                         border: 0, cursor: "pointer", fontSize: 13,
                         fontWeight: 500,
                         color: active === t.k ? INDIGO : "#1C1C1C",
                         background: active === t.k ? "white" : "transparent",
                         borderBottom: active === t.k ? `2px solid ${INDIGO}` : "2px solid transparent" }}>
          {t.label}
        </button>
      ))}
    </div>
  );
}

/* ----- Overview tab (snapshot KPIs + signups trend) ----- */
function OverviewTab({ params, exportSignupsCsv }) {
  const snap = useAdminQuery("/mis/snapshot", params);
  const signups = useAdminQuery("/mis/signups", params);
  const users = useAdminQuery("/mis/signups/users", { ...params, limit: 25, skip: 0 });
  if (snap.error) return <Unavailable error={snap.error} />;
  const k = snap.data?.kpis || {}, p = snap.data?.previous || {};
  const delta = (a, b) => {
    const A = Number(a || 0), B = Number(b || 0);
    if (!B) return A ? "new" : "—";
    const pct = Math.round((A - B) * 1000.0 / B) / 10;
    return `${pct >= 0 ? "+" : ""}${pct}% vs prev`;
  };
  return (
    <>
      <KpiGrid>
        <KpiCard label="New Signups" value={k.signups ?? "—"}
                   hint={delta(k.signups, p.signups)} testId="mis-kpi-signups" />
        <KpiCard label="DAU" value={k.dau ?? "—"} hint={delta(k.dau, p.dau)}
                   testId="mis-kpi-dau" />
        <KpiCard label="Questions" value={k.questions ?? "—"}
                   hint={delta(k.questions, p.questions)}
                   testId="mis-kpi-questions" />
        <KpiCard label="Reports Completed"
                   value={k.reports_completed ?? "—"}
                   hint={delta(k.reports_completed, p.reports_completed)}
                   testId="mis-kpi-reports" />
        <KpiCard label="Credits Granted"
                   value={k.credits_granted ?? "—"}
                   testId="mis-kpi-credits-granted" />
        <KpiCard label="Credits Spent" value={k.credits_spent ?? "—"}
                   testId="mis-kpi-credits-spent" />
        <KpiCard label="👍 Thumbs Up" value={k.thumbs_up ?? "—"}
                   testId="mis-kpi-up" />
        <KpiCard label="👎 Thumbs Down" value={k.thumbs_down ?? "—"}
                   testId="mis-kpi-down" />
        <KpiCard label="Validator Failures"
                   value={k.validator_failures ?? "—"}
                   testId="mis-kpi-validator" />
      </KpiGrid>
      <Panel title="Daily signups (IST)" testId="mis-panel-trend">
        <div style={{ display: "flex", gap: 6, alignItems: "flex-end",
                      height: 140, padding: "8px 4px",
                      overflowX: "auto" }}>
          {arr(signups.data?.series).map((pt, idx) => {
            const s = arr(signups.data?.series);
            const max = Math.max(1, ...s.map((x) => x.count || 0));
            const h = Math.max(2, ((pt.count || 0) / max) * 120);
            return (
              <div key={pt.day || `d-${idx}`} style={{ flex: "0 0 auto",
                      minWidth: 22, display: "flex", flexDirection: "column",
                      alignItems: "center", gap: 4 }}>
                <div style={{ fontSize: 10, color: MUTE }}>{pt.count}</div>
                <div style={{ width: 18, height: h,
                               background: INDIGO, borderRadius: 4,
                               opacity: pt.count === 0 ? 0.2 : 1 }} />
                <div style={{ fontSize: 9, color: MUTE,
                               whiteSpace: "nowrap" }}>{(pt.day||"").slice(5)}</div>
              </div>
            );
          })}
        </div>
      </Panel>
      <Panel title={`Signup drill-down (${users.data?.total ?? 0})`}
               testId="mis-panel-drilldown"
               right={<button data-testid="mis-export-csv"
                                 onClick={exportSignupsCsv}
                                 style={{ padding: "6px 14px",
                                           borderRadius: 999,
                                           background: INDIGO,
                                           color: "white", border: 0,
                                           cursor: "pointer",
                                           fontSize: 11.5, fontWeight: 500 }}>
                           Export CSV</button>}>
        <Table testId="mis-signups-table"
               cols={[
                 { k: "name", h: "Name" },
                 { k: "email", h: "Email" },
                 { k: "user_id", h: "User ID" },
                 { k: "created_at_ist", h: "Signup (IST)" },
                 { k: "last_seen_ist", h: "Last seen (IST)" },
               ]}
               rows={arr(users.data?.items)} />
      </Panel>
    </>
  );
}

/* ----- Funnel tab ----- */
function FunnelTab({ params }) {
  const q = useAdminQuery("/mis/funnel", params);
  if (q.error) return <Unavailable error={q.error} />;
  const stages = arr(q.data?.stages);
  return (
    <Panel title={`Conversion Funnel · cohort ${q.data?.cohort_size ?? 0}`}
             testId="mis-panel-funnel">
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {stages.map((s, idx) => {
          const pct = s.pct_of_cohort ?? 0;
          return (
            <div key={s.stage || idx} data-testid={`mis-funnel-${s.stage}`}
                 style={{ padding: 12, borderRadius: 10, background: "white",
                          border: "1px solid #EEEBF5" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8,
                             flexWrap: "wrap" }}>
                <div style={{ fontSize: 12, textTransform: "uppercase",
                               color: MUTE, letterSpacing: "0.04em",
                               minWidth: 160 }}>
                  {s.stage.replace(/_/g, " ")}
                </div>
                <div style={{ flex: 1, height: 10, background: "#F2EEFF",
                               borderRadius: 999, overflow: "hidden" }}>
                  <div style={{ width: `${Math.max(2, pct)}%`,
                                 height: "100%", background: INDIGO }} />
                </div>
                <div style={{ fontWeight: 600, color: "#1C1C1C", minWidth: 60,
                               textAlign: "right" }}>{s.users}</div>
                <div style={{ fontSize: 11, color: MUTE, minWidth: 100,
                               textAlign: "right" }}>
                  {s.pct_of_cohort ?? "—"}% cohort · {s.pct_of_previous ?? "—"}% prev
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </Panel>
  );
}

/* ----- Retention tab ----- */
function RetentionTab({ params }) {
  const q = useAdminQuery("/mis/retention", params);
  if (q.error) return <Unavailable error={q.error} />;
  return (
    <Panel title="Signup-day retention cohorts (IST)"
             testId="mis-panel-retention">
      <Table testId="mis-retention-table"
             cols={[
               { k: "cohort", h: "Cohort day" },
               { k: "size", h: "Users" },
               { k: "d1_display", h: "D1" },
               { k: "d7_display", h: "D7" },
               { k: "d30_display", h: "D30" },
             ]}
             rows={arr(q.data?.cohorts).map((c) => ({
               ...c,
               d1_display: c.d1_eligible ? `${c.d1_pct ?? 0}% (${c.d1_retained}/${c.d1_eligible})` : "—",
               d7_display: c.d7_eligible ? `${c.d7_pct ?? 0}% (${c.d7_retained}/${c.d7_eligible})` : "—",
               d30_display: c.d30_eligible ? `${c.d30_pct ?? 0}% (${c.d30_retained}/${c.d30_eligible})` : "—",
             }))} />
    </Panel>
  );
}

/* ----- Users directory tab ----- */
function UsersTab({ exclude }) {
  const [q, setQ] = useState("");
  const [coupon, setCoupon] = useState("");
  const params = useMemo(() => ({
    q: q || undefined, coupon: coupon || undefined,
    exclude_internal: exclude, limit: 50, skip: 0,
  }), [q, coupon, exclude]);
  const r = useAdminQuery("/mis/users", params);
  if (r.error) return <Unavailable error={r.error} />;
  return (
    <Panel title={`User directory (${r.data?.total ?? 0})`}
             testId="mis-panel-users">
      <div style={{ display: "flex", gap: 8, marginBottom: 10, flexWrap: "wrap" }}>
        <input data-testid="mis-users-q" value={q}
               placeholder="search name/email/user_id"
               onChange={(e) => setQ(e.target.value)}
               style={{ flex: "1 1 220px", padding: "8px 12px",
                        borderRadius: 10, border: "1px solid #E5E1F5",
                        fontSize: 13 }}/>
        <input data-testid="mis-users-coupon" value={coupon}
               placeholder="coupon code (exact)"
               onChange={(e) => setCoupon(e.target.value)}
               style={{ width: 200, padding: "8px 12px",
                        borderRadius: 10, border: "1px solid #E5E1F5",
                        fontSize: 13 }}/>
      </div>
      <Table testId="mis-users-table"
             cols={[
               { k: "name", h: "Name" },
               { k: "email", h: "Email" },
               { k: "credits_balance", h: "Credits" },
               { k: "questions", h: "Qs" },
               { k: "reports", h: "Reports" },
               { k: "coupon_code", h: "Coupon" },
               { k: "created_at_ist", h: "Signup (IST)" },
             ]}
             rows={arr(r.data?.items)} />
    </Panel>
  );
}

/* ----- Coupons & Credits tab ----- */
function CouponsTab({ exclude }) {
  const coupons = useAdminQuery("/mis/coupons");
  const credits = useAdminQuery("/mis/credits/summary",
                                   { preset: "30d", exclude_internal: exclude });
  if (coupons.error) return <Unavailable error={coupons.error} />;
  const sum = credits.data || {};
  return (
    <>
      <KpiGrid>
        <KpiCard label="Credits granted (30d)" value={sum.granted ?? "—"}
                   testId="mis-cr-granted" />
        <KpiCard label="Credits spent (30d)" value={sum.spent ?? "—"}
                   testId="mis-cr-spent" />
      </KpiGrid>
      <Panel title={`Coupons (${coupons.data?.count ?? 0})`}
               testId="mis-panel-coupons">
        <Table testId="mis-coupons-table"
               cols={[
                 { k: "code", h: "Code" },
                 { k: "credits", h: "Credits/each" },
                 { k: "redemptions", h: "Redeemed" },
                 { k: "unique_redeemers", h: "Unique users" },
                 { k: "max_total_redemptions", h: "Cap" },
                 { k: "active", h: "Active" },
                 { k: "expires_at", h: "Expires" },
               ]}
               rows={arr(coupons.data?.items)} />
      </Panel>
    </>
  );
}

/* ----- Engine Health tab (Phase 4) ----- */
function EngineTab({ exclude }) {
  const val = useAdminQuery("/mis/engine/validator",
                               { preset: "7d", exclude_internal: exclude });
  const gold = useAdminQuery("/mis/engine/golden");
  if (val.error) return <Unavailable error={val.error} />;
  const issues = Object.entries(val.data?.by_issue || {}).sort(
    (a, b) => b[1] - a[1]).slice(0, 15);
  return (
    <>
      <KpiGrid>
        <KpiCard label="Validator failures (7d)"
                   value={val.data?.total_failures ?? "—"}
                   testId="mis-eng-total" />
        <KpiCard label="Golden Test"
                   value={gold.data?.status || "NOT_RUN"}
                   hint={gold.data?.note || ""}
                   testId="mis-eng-golden" />
      </KpiGrid>
      <Panel title="Top validator issues"
               testId="mis-panel-validator-issues">
        <Table testId="mis-validator-issues"
               cols={[
                 { k: "issue", h: "Issue" },
                 { k: "count", h: "Count" },
               ]}
               rows={issues.map(([issue, count]) => ({ issue, count }))} />
      </Panel>
      <Panel title="Daily validator failures" testId="mis-panel-validator-series">
        <Table testId="mis-validator-series"
               cols={[{ k: "day", h: "Day" }, { k: "count", h: "Failures" }]}
               rows={arr(val.data?.series)} />
      </Panel>
    </>
  );
}

/* ----- Daily Digest tab (Phase 6) ----- */
function DigestTab() {
  const recipients = useAdminQuery("/mis/digest/recipients");
  const runs = useAdminQuery("/mis/digest/runs");
  const [emails, setEmails] = useState("");
  const [sending, setSending] = useState(false);
  React.useEffect(() => {
    if (recipients.data?.emails) setEmails(recipients.data.emails.join(", "));
  }, [recipients.data]);
  const saveRecipients = async () => {
    try {
      const list = emails.split(/[,\s]+/).map((s) => s.trim()).filter((s) => s.includes("@"));
      const { data } = await api.post("/mis/digest/recipients", { emails: list });
      toast.success(`Saved ${data.emails.length} recipient(s).`);
      recipients.refresh();
    } catch (e) { toast.error("Save failed."); }
  };
  const sendNow = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/mis/digest/send");
      toast.success(`Digest queued to ${(data.sent || []).length} recipient(s).`);
      runs.refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed.");
    } finally { setSending(false); }
  };
  return (
    <>
      <Panel title="Daily 9pm IST digest · recipients"
               testId="mis-panel-digest-recipients">
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <input data-testid="mis-digest-emails" value={emails}
                 placeholder="founder@kalki.app, cofounder@kalki.app"
                 onChange={(e) => setEmails(e.target.value)}
                 style={{ flex: "1 1 300px", padding: "8px 12px",
                          borderRadius: 10, border: "1px solid #E5E1F5",
                          fontSize: 13 }} />
          <button data-testid="mis-digest-save" onClick={saveRecipients}
                  style={{ padding: "8px 16px", borderRadius: 999,
                           background: INDIGO, color: "white", border: 0,
                           cursor: "pointer", fontSize: 12, fontWeight: 500 }}>
            Save recipients
          </button>
          <button data-testid="mis-digest-send-now" onClick={sendNow}
                  disabled={sending}
                  style={{ padding: "8px 16px", borderRadius: 999,
                           background: "white", color: INDIGO,
                           border: `1px solid ${INDIGO}`, cursor: "pointer",
                           fontSize: 12, fontWeight: 500 }}>
            {sending ? "Sending…" : "Send digest now"}
          </button>
        </div>
        <p style={{ marginTop: 10, fontSize: 11, color: MUTE }}>
          The MIS cron fires every day at 21:00 IST. "Send digest now" is a manual
          dry-run that reuses the exact 9pm render + Resend transport.
        </p>
      </Panel>
      <Panel title="Recent digest runs" testId="mis-panel-digest-runs">
        <Table testId="mis-digest-runs"
               cols={[
                 { k: "day", h: "Day" },
                 { k: "day_label", h: "Label" },
                 { k: "ran_at", h: "Ran (UTC)" },
                 { k: "n_recipients", h: "Recipients" },
               ]}
               rows={arr(runs.data?.items).map((r) => ({
                 ...r,
                 n_recipients: (r.recipients || []).length,
               }))} />
      </Panel>
    </>
  );
}

/* ----- Internal Accounts tab (Phase 1 registry) ----- */
function RegistryTab() {
  const internals = useAdminQuery("/mis/internal-accounts");
  const [uid, setUid] = useState("");
  const [kind, setKind] = useState("internal");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!uid.trim()) return;
    setBusy(true);
    try {
      await api.post("/mis/internal-accounts",
        { user_id: uid.trim(), kind, note: null });
      toast.success("Tagged.");
      setUid("");
      internals.refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to tag.");
    } finally { setBusy(false); }
  };
  const remove = async (user_id) => {
    try {
      await api.delete(`/mis/internal-accounts/${user_id}`);
      internals.refresh();
    } catch (e) { toast.error("Remove failed."); }
  };
  return (
    <Panel title={`Internal & Test accounts (${internals.data?.count ?? 0})`}
             testId="mis-panel-internals">
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 10 }}>
        <input data-testid="mis-tag-uid" value={uid}
               placeholder="user_id to tag"
               onChange={(e) => setUid(e.target.value)}
               style={{ flex: "1 1 200px", padding: "8px 12px",
                        borderRadius: 10, border: "1px solid #E5E1F5",
                        fontSize: 13 }} />
        <select value={kind} onChange={(e) => setKind(e.target.value)}
                data-testid="mis-tag-kind"
                style={{ padding: "8px 12px", borderRadius: 10,
                         border: "1px solid #E5E1F5", fontSize: 13 }}>
          <option value="internal">internal</option>
          <option value="test">test</option>
        </select>
        <button data-testid="mis-tag-add" onClick={submit} disabled={busy}
                style={{ padding: "8px 16px", borderRadius: 999,
                         background: INDIGO, color: "white", border: 0,
                         cursor: "pointer", fontSize: 12, fontWeight: 500 }}>
          {busy ? "Adding…" : "Tag account"}
        </button>
      </div>
      <Table testId="mis-internals-table"
             cols={[
               { k: "user_id", h: "User ID" },
               { k: "email", h: "Email" },
               { k: "kind", h: "Kind" },
               { k: "tagged_by", h: "Tagged by" },
               { k: "created_at", h: "Tagged at (UTC)" },
             ]}
             rows={arr(internals.data?.items).map((it) => ({
               ...it,
               action: (
                 <button data-testid={`mis-untag-${it.user_id}`}
                         onClick={() => remove(it.user_id)}
                         style={{ background: "transparent", border: 0,
                                  color: INDIGO, cursor: "pointer",
                                  fontSize: 11 }}>remove</button>
               ),
             }))} />
    </Panel>
  );
}

export default function AdminMis() {
  const [preset, setPreset] = useState("7d");
  const [exclude, setExclude] = useState(true);
  const [tab, setTab] = useState("overview");
  const params = useMemo(() => ({ preset, exclude_internal: exclude }), [preset, exclude]);
  const sections = useAdminQuery("/mis/sections");
  const snapRefresh = useAdminQuery("/mis/health");

  const exportSignupsCsv = useCallback(async () => {
    const q = new URLSearchParams({ preset,
      exclude_internal: exclude ? "true" : "false" }).toString();
    try {
      const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
      const r = await fetch(`${base}/api/mis/signups/export.csv?${q}`,
                              { credentials: "include" });
      if (!r.ok) throw new Error("export_failed");
      const blob = await r.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `kalki_signups_${preset}.csv`;
      a.click();
    } catch (_) { toast.error("Export failed."); }
  }, [preset, exclude]);

  return (
    <div data-testid="admin-mis">
      <PageHeader title="Management Intelligence"
        subtitle={`Phase 1+2+4+6 · tracking since ${sections.data?.tracking_since || "—"}`}
        right={<RefreshButton loading={snapRefresh.loading}
                                onClick={() => snapRefresh.refresh()} />} />
      <FilterBar preset={preset} setPreset={setPreset}
                   exclude={exclude} setExclude={setExclude} />
      <TabBar active={tab} setActive={setTab} />
      {tab === "overview"  && <OverviewTab params={params} exportSignupsCsv={exportSignupsCsv} />}
      {tab === "funnel"    && <FunnelTab params={params} />}
      {tab === "retention" && <RetentionTab params={params} />}
      {tab === "users"     && <UsersTab exclude={exclude} />}
      {tab === "coupons"   && <CouponsTab exclude={exclude} />}
      {tab === "engine"    && <EngineTab exclude={exclude} />}
      {tab === "digest"    && <DigestTab />}
      {tab === "registry"  && <RegistryTab />}
    </div>
  );
}
